/**
 * Agent Board Redux Reducer
 * Manages inter-agent communication board state.
 * 
 * State:
 *   - boardMessages: array of AgentBoardMessageData (in message order)
 *   - boardMessagesByType: map of type -> messages
 *   - boardStatus: 'idle' | 'loading' | 'connected' | 'error'
 *   - unreadCount: number of unread agent-board messages
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AgentBoardMessageData {
  _id: string;
  senderAgentId: string;
  senderName: string;
  senderType: 'agent' | 'orchestrator' | 'system';
  content: string | Record<string, unknown>;
  type: 'agent_message' | 'tool_call' | 'tool_result' | 'assignment' | 'system';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  toolCall?: {
    toolKey: string;
    toolName: string;
    args: Record<string, unknown>;
    result?: unknown;
    error?: string;
  };
  assignment?: {
    taskId: string;
    taskDescription: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    dependencies: string[];
  };
  phase?: 'detect' | 'plan' | 'assign' | 'execute' | 'complete' | 'failed';
  createdAt: string;
  updatedAt: string;
}

export interface AgentBoardState {
  boardMessages: AgentBoardMessageData[];
  boardMessagesByType: Record<string, AgentBoardMessageData[]>;
  boardStatus: 'idle' | 'loading' | 'connected' | 'error';
  unreadCount: number;
  lastMessageId: string | null;
}

const initialState: AgentBoardState = {
  boardMessages: [],
  boardMessagesByType: {
    agent_message: [],
    tool_call: [],
    tool_result: [],
    assignment: [],
    system: [],
  },
  boardStatus: 'idle',
  unreadCount: 0,
  lastMessageId: null,
};

const agentBoardSlice = createSlice({
  name: 'agentBoard',
  initialState,
  reducers: {
    setBoardStatus(state, action: PayloadAction<AgentBoardState['boardStatus']>) {
      state.boardStatus = action.payload;
    },

    addBoardMessage(state, action: PayloadAction<AgentBoardMessageData>) {
      const msg = action.payload;
      state.boardMessages.unshift(msg);
      state.lastMessageId = msg._id;
      
      if (!msg.type || !state.boardMessagesByType[msg.type]) {
        state.boardMessagesByType[msg.type] = [];
      }
      state.boardMessagesByType[msg.type].unshift(msg);

      if (msg.senderType !== 'orchestrator') {
        state.unreadCount++;
      }
    },

    updateBoardMessage(state, action: PayloadAction<{ id: string; updates: Partial<AgentBoardMessageData> }>) {
      const { id, updates } = action.payload;
      
      const idx = state.boardMessages.findIndex(m => m._id === id);
      if (idx !== -1) {
        state.boardMessages[idx] = { ...state.boardMessages[idx], ...updates };
      }
    },

    removeBoardMessage(state, action: PayloadAction<string>) {
      state.boardMessages = state.boardMessages.filter(m => m._id !== action.payload);
      Object.keys(state.boardMessagesByType).forEach(type => {
        state.boardMessagesByType[type] = state.boardMessagesByType[type].filter(m => m._id !== action.payload);
      });
    },

    clearBoardMessages(state) {
      state.boardMessages = [];
      Object.keys(state.boardMessagesByType).forEach(type => {
        state.boardMessagesByType[type] = [];
      });
      state.unreadCount = 0;
      state.lastMessageId = null;
    },

    resetUnreadCount(state) {
      state.unreadCount = 0;
    },

    syncBoardMessages(state, action: PayloadAction<AgentBoardMessageData[]>) {
      state.boardMessages = action.payload;
      state.boardMessagesByType = {
        agent_message: [],
        tool_call: [],
        tool_result: [],
        assignment: [],
        system: [],
      };
      action.payload.forEach(msg => {
        if (msg.type && state.boardMessagesByType[msg.type]) {
          state.boardMessagesByType[msg.type].push(msg);
        }
      });
      state.unreadCount = 0;
    },
  },
});

export const {
  setBoardStatus,
  addBoardMessage,
  updateBoardMessage,
  removeBoardMessage,
  clearBoardMessages,
  resetUnreadCount,
  syncBoardMessages,
} = agentBoardSlice.actions;

export default agentBoardSlice.reducer;
